import React from 'react';
import { Link } from 'react-router-dom';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';
import SearchHeader from '../../../components/ui/SearchHeader';

const CategoryHero = ({ category, onSearch }) => {
  const categoryData = {
    'photography': {
      title: 'Wedding Photography',
      cultural: 'Capturing Your Sacred Moments',
      description: 'Professional photographers specializing in South Asian wedding traditions, from intimate Mehndi ceremonies to grand reception celebrations.',
      heroImage: 'https://images.unsplash.com/photo-1606216794074-735e91aa2c92?w=1200&h=600&fit=crop',
      stats: { vendors: 156, bookings: '2.3K+', rating: 4.8 },
      culturalContext: `Wedding photography in South Asian culture captures not just moments, but the essence of sacred traditions. From the intricate details of Mehndi designs to the emotional moments of Bidaai, our photographers understand the cultural significance of each ritual.`
    },
    'videography': {
      title: 'Wedding Videography',
      cultural: 'Your Story, Beautifully Told',
      description: 'Professional videographers capturing cinematic wedding films that preserve your precious moments for generations to come.',
      heroImage: 'https://images.unsplash.com/photo-1492691527719-9d1e07e534b4?w=1200&h=600&fit=crop',
      stats: { vendors: 98, bookings: '1.5K+', rating: 4.7 },
      culturalContext: `Wedding videography brings your love story to life, capturing the emotions, colors, and energy of your celebration. From the sacred vows to the joyous sangeet, every moment is preserved forever.`
    },
    'catering': {
      title: 'Wedding Catering',
      cultural: 'Authentic Flavors & Traditions',
      description: 'Expert caterers specializing in traditional South Asian cuisine, from elaborate multi-course meals to fusion menus.',
      heroImage: 'https://images.pexels.com/photos/1640777/pexels-photo-1640777.jpeg?w=1200&h=600&fit=crop',
      stats: { vendors: 142, bookings: '2.8K+', rating: 4.8 },
      culturalContext: `Food is central to South Asian celebrations. Our caterers honor regional cuisines and dietary traditions while creating memorable culinary experiences for your guests.`
    },
    'decoration': {
      title: 'Wedding Decoration',
      cultural: 'Creating Magical Spaces',
      description: 'Talented decorators transforming venues with stunning mandap setups, floral arrangements, and traditional decor elements.',
      heroImage: 'https://images.unsplash.com/photo-1519167758481-83f550bb49b3?w=1200&h=600&fit=crop',
      stats: { vendors: 76, bookings: '1.2K+', rating: 4.9 },
      culturalContext: `Wedding decor sets the tone for your celebration, from elaborate mandap designs to elegant floral arrangements that honor your cultural heritage.`
    },
    'music': {
      title: 'Wedding Music & DJs',
      cultural: 'Bollywood & Bhangra Specialists',
      description: 'Professional DJs and musicians mixing the best of Bollywood, Bhangra, and Western hits to keep your celebration dancing all night.',
      heroImage: 'https://images.unsplash.com/photo-1493225457124-a3eb161ffa5f?w=1200&h=600&fit=crop',
      stats: { vendors: 124, bookings: '3.1K+', rating: 4.7 },
      culturalContext: `Music is the heartbeat of South Asian celebrations. Our DJs understand how to seamlessly blend traditional folk songs with modern Bollywood hits, creating the perfect soundtrack for each ceremony.`
    },
    'makeup': {
      title: 'Bridal Makeup',
      cultural: 'Enhancing Your Natural Beauty',
      description: 'Skilled makeup artists specializing in South Asian bridal looks, from traditional to contemporary styles.',
      heroImage: 'https://images.unsplash.com/photo-1487412947147-5cebf100ffc2?w=1200&h=600&fit=crop',
      stats: { vendors: 112, bookings: '2.1K+', rating: 4.9 },
      culturalContext: `Bridal makeup is an art form that enhances your natural beauty while respecting cultural traditions. Our artists create stunning looks that last throughout your celebration.`
    },
    'mehndi': {
      title: 'Mehndi Artists',
      cultural: 'Traditional Henna Artistry',
      description: 'Skilled Mehndi artists creating intricate henna designs for brides and wedding parties, specializing in traditional and contemporary patterns.',
      heroImage: 'https://images.unsplash.com/photo-1583391733956-6c78276477e2?w=1200&h=600&fit=crop',
      stats: { vendors: 89, bookings: '1.8K+', rating: 4.9 },
      culturalContext: `Mehndi is more than body art—it's a celebration of love, fertility, and new beginnings. Our artists specialize in regional styles from intricate Rajasthani patterns to elegant Arabic designs.`
    },
    'venue': {
      title: 'Wedding Venues',
      cultural: 'Sacred Spaces for Celebrations',
      description: 'Beautiful venues accommodating South Asian wedding traditions, from intimate Mandap ceremonies to grand reception halls.',
      heroImage: 'https://images.unsplash.com/photo-1519167758481-83f550bb49b3?w=1200&h=600&fit=crop',
      stats: { vendors: 67, bookings: '890+', rating: 4.6 },
      culturalContext: `The right venue honors your traditions while accommodating your vision. Our venues understand the space requirements for Mandap setups, Agni ceremonies, and multi-day celebrations.`
    },
    'planning': {
      title: 'Wedding Planning',
      cultural: 'Expert Coordination & Management',
      description: 'Professional wedding planners who understand South Asian traditions and coordinate every detail of your celebration.',
      heroImage: 'https://images.unsplash.com/photo-1464366400600-7168b8af9bc3?w=1200&h=600&fit=crop',
      stats: { vendors: 54, bookings: '780+', rating: 4.8 },
      culturalContext: `Wedding planning for South Asian celebrations requires cultural knowledge and attention to detail. Our planners coordinate multi-day events seamlessly.`
    },
    'other': {
      title: 'Other Wedding Services',
      cultural: 'Complete Your Celebration',
      description: 'Additional wedding services including transportation, favors, invitations, and more to complete your perfect day.',
      heroImage: 'https://images.unsplash.com/photo-1511285560929-80b456fea0bc?w=1200&h=600&fit=crop',
      stats: { vendors: 45, bookings: '650+', rating: 4.7 },
      culturalContext: `From baraat horses to wedding favors, every detail matters. Our vendors provide those special touches that make your wedding uniquely yours.`
    }
  };

  const currentCategory = categoryData[category] || categoryData['photography'];

  return (
    <div className="relative bg-gradient-to-br from-primary-50 to-secondary-50 overflow-hidden">
      {/* Background Pattern */}
      <div className="absolute inset-0 opacity-5">
        <div className="absolute inset-0 bg-repeat" style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23000000' fill-opacity='0.1'%3E%3Cpath d='M30 30c0-11.046-8.954-20-20-20s-20 8.954-20 20 8.954 20 20 20 20-8.954 20-20zm0 0c0 11.046 8.954 20 20 20s20-8.954 20-20-8.954-20-20-20-20 8.954-20 20z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`
        }} />
      </div>

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 lg:py-20">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          {/* Content */}
          <div className="space-y-8">
            {/* Breadcrumb */}
            <nav className="flex items-center space-x-2 text-sm font-caption">
              <Link to="/" className="text-text-secondary hover:text-primary transition-colors">
                Home
              </Link>
              <Icon name="ChevronRight" size={14} color="#6B5B4F" />
              <Link to="/browse-categories-all-vendor-types" className="text-text-secondary hover:text-primary transition-colors">
                Categories
              </Link>
              <Icon name="ChevronRight" size={14} color="#6B5B4F" />
              <span className="text-primary font-medium">{currentCategory.title}</span>
            </nav>

            {/* Title */}
            <div className="space-y-4">
              <h1 className="font-heading font-bold text-4xl lg:text-5xl text-text-primary">
                {currentCategory.title}
              </h1>
              <p className="text-xl text-secondary font-heading font-semibold">
                {currentCategory.cultural}
              </p>
              <p className="text-lg text-text-secondary leading-relaxed">
                {currentCategory.description}
              </p>
            </div>

            {/* Stats */}
            <div className="flex items-center space-x-8">
              <div className="text-center">
                <div className="font-heading font-bold text-2xl text-primary">
                  {currentCategory.stats.vendors}
                </div>
                <div className="text-sm text-text-secondary font-caption">Verified Vendors</div>
              </div>
              <div className="text-center">
                <div className="font-heading font-bold text-2xl text-secondary">
                  {currentCategory.stats.bookings}
                </div>
                <div className="text-sm text-text-secondary font-caption">Happy Couples</div>
              </div>
              <div className="text-center">
                <div className="flex items-center justify-center space-x-1">
                  <Icon name="Star" size={20} color="#DAA520" />
                  <span className="font-heading font-bold text-2xl text-secondary">
                    {currentCategory.stats.rating}
                  </span>
                </div>
                <div className="text-sm text-text-secondary font-caption">Average Rating</div>
              </div>
            </div>

            {/* Search */}
            <div className="pt-4">
              <SearchHeader
                variant="default"
                onSearch={onSearch}
                placeholder={`Search ${currentCategory.title.toLowerCase()}...`}
              />
            </div>
          </div>

          {/* Hero Image */}
          <div className="relative">
            <div className="relative overflow-hidden rounded-2xl shadow-strong">
              <Image
                src={currentCategory.heroImage}
                alt={`${currentCategory.title} showcase`}
                className="w-full h-96 lg:h-[500px] object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/30 to-transparent" />

              {/* Floating Badge */}
              <div className="absolute top-6 right-6 bg-white/95 backdrop-blur-sm rounded-lg px-4 py-2 shadow-medium">
                <div className="flex items-center space-x-2">
                  <Icon name="Verified" size={16} color="#228B22" />
                  <span className="text-sm font-medium text-text-primary">Verified Vendors</span>
                </div>
              </div>
            </div>

            {/* Cultural Context Card */}
            <div className="absolute -bottom-6 left-6 right-6 bg-white rounded-xl shadow-strong p-6 border border-border-light">
              <div className="flex items-start space-x-3">
                <div className="flex-shrink-0 w-8 h-8 bg-primary-50 rounded-lg flex items-center justify-center">
                  <Icon name="Info" size={16} color="#8B4513" />
                </div>
                <div>
                  <h3 className="font-heading font-semibold text-text-primary mb-2">
                    Cultural Significance
                  </h3>
                  <p className="text-sm text-text-secondary leading-relaxed font-caption">
                    {currentCategory.culturalContext}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CategoryHero;